import { toDecimals } from './format';

export interface CommissionInfoResult {
  externalCode: string | null;
  userExternalCode: string | null;
  initialDiscountPercent: number | null;
  finalDiscountPercent: number | null;
  userName: string | null;
  commissionType: number | null;
  percentage: number | null;
  value: number | null;
  fromPromotion: boolean | null;
}

export interface CalculateCommissionParams {
  allowUserCommission: boolean;
  commissions: {
    externalCode: string;
    userExternalCode: string;
    initialDiscountPercent: number;
    finalDiscountPercent: number;
    percentage: number;
    userName: string;
    commissionType: number;
  }[];
  promotionCommission?: number | null;
  discountPercentage: number;
  soldPrice: number;
  soldQuantity: number;
  decimals: number;
}

export const calculateCommissionInfo = ({
  allowUserCommission,
  commissions,
  promotionCommission,
  discountPercentage,
  soldPrice,
  soldQuantity,
  decimals
}: CalculateCommissionParams): CommissionInfoResult => {
  if (!allowUserCommission) {
    return {
      externalCode: null,
      userExternalCode: null,
      initialDiscountPercent: null,
      finalDiscountPercent: null,
      userName: null,
      commissionType: null,
      percentage: 0,
      value: 0,
      fromPromotion: null
    };
  }

  const totalSale = toDecimals(soldPrice * soldQuantity, decimals ?? 2);

  if (promotionCommission) {
    const commissionValue = toDecimals((promotionCommission / 100) * totalSale, decimals ?? 2) || 0;

    return {
      externalCode: null,
      userExternalCode: null,
      initialDiscountPercent: null,
      finalDiscountPercent: null,
      userName: null,
      commissionType: null,
      percentage: promotionCommission,
      value: commissionValue,
      fromPromotion: true
    };
  }

  const discount = discountPercentage || 0;

  if (!commissions || commissions.length === 0) {
    return {
      externalCode: null,
      userExternalCode: null,
      initialDiscountPercent: null,
      finalDiscountPercent: null,
      userName: null,
      commissionType: null,
      percentage: 0,
      value: 0,
      fromPromotion: false
    };
  }

  // Filtrar faixas que atendem ao desconto atual
  const matchingRanges = commissions.filter(
    commission => discount >= commission.initialDiscountPercent && discount <= commission.finalDiscountPercent
  );

  if (matchingRanges.length === 0) {
    return {
      externalCode: null,
      userExternalCode: null,
      initialDiscountPercent: null,
      finalDiscountPercent: null,
      userName: null,
      commissionType: null,
      percentage: 0,
      value: 0,
      fromPromotion: false
    };
  }

  // Se houver múltiplas faixas, pegar a com maior desconto máximo
  const selectedRange = matchingRanges.reduce((prev, current) => {
    return current.finalDiscountPercent > prev.finalDiscountPercent ? current : prev;
  });

  const commissionValue = toDecimals((selectedRange.percentage / 100) * totalSale, decimals ?? 2) || 0;

  return {
    ...selectedRange,
    value: commissionValue,
    fromPromotion: false
  };
};
